import * as React from "react"

import { cn } from "../../cn"
import { Label, type LabelProps } from "../Label/Label"

type SettingRowControl = (
  controlId: string,
  describedBy: { "aria-describedby": string | undefined },
) => React.ReactNode

interface SettingRowBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  label: React.ReactNode
  tooltip?: React.ReactNode
  /** Icon that opens the tooltip; defaults to "info". */
  tooltipIcon?: LabelProps["tooltipIcon"]
  /** Renders the control under the label instead of beside it. */
  stacked?: boolean
  /** For a stacked row: something small to sit at the right of the label. */
  action?: React.ReactNode
  /** Shown under the row in the danger colour. */
  error?: React.ReactNode
}

/**
 * The control is either a node or a render function that receives the id to
 * put on the control, so the label is associated with it, and the attributes
 * that tie the description to it. A row with a description takes the render
 * function, because only the control can carry `aria-describedby`.
 */
export type SettingRowProps = SettingRowBaseProps &
  (
    | {
        description?: undefined
        children: React.ReactNode | SettingRowControl
      }
    | {
        /** Muted guidance under the label, announced as the control's description. */
        description: React.ReactNode
        children: SettingRowControl
      }
  )

/**
 * One setting in a settings list: label and help icon on the left, control on
 * the right. Every row shares this shape so a panel of toggles and selects
 * scans as settings rather than as a form.
 */
const SettingRow = React.forwardRef<HTMLDivElement, SettingRowProps>(
  (
    {
      label,
      tooltip,
      tooltipIcon = "info",
      description,
      stacked,
      action,
      error,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const controlId = React.useId()
    const descriptionId = `${controlId}-description`
    const hasDescription =
      description != null &&
      typeof description !== "boolean" &&
      description !== ""
    const control =
      typeof children === "function"
        ? children(controlId, {
            "aria-describedby": hasDescription ? descriptionId : undefined,
          })
        : children
    const labelText = (
      <Label
        htmlFor={controlId}
        tooltip={
          tooltip ? <div className="max-w-xs">{tooltip}</div> : undefined
        }
        tooltipIcon={tooltipIcon}
      >
        {label}
      </Label>
    )
    const labelNode = hasDescription ? (
      <div className="min-w-0 space-y-0.5">
        {labelText}
        <p id={descriptionId} className="text-xs text-coval-text-muted">
          {description}
        </p>
      </div>
    ) : (
      labelText
    )

    if (stacked) {
      return (
        <div ref={ref} className={cn("space-y-2 py-2", className)} {...props}>
          <div className="flex min-h-8 items-center justify-between gap-6">
            {labelNode}
            {action}
          </div>
          {control}
          {error && <p className="text-xs text-coval-danger-text">{error}</p>}
        </div>
      )
    }

    return (
      <div ref={ref} className={cn("py-1", className)} {...props}>
        <div className="flex min-h-12 items-center justify-between gap-6">
          {labelNode}
          <div className="flex shrink-0 items-center gap-3">{control}</div>
        </div>
        {error && <p className="text-xs text-coval-danger-text">{error}</p>}
      </div>
    )
  },
)

SettingRow.displayName = "SettingRow"

export { SettingRow }
